// Persistimos el "saltar" en localStorage (no sessionStorage): el cliente no
// quiere que el onboarding reaparezca en cada pestaña/sesión nueva una vez lo
// ha descartado conscientemente.
const SKIP_KEY = 'onb.skipped';

export function isOnboardingSkipped(): boolean {
  try {
    return localStorage.getItem(SKIP_KEY) === '1';
  } catch {
    return false;
  }
}

export function markOnboardingSkipped(): void {
  try {
    localStorage.setItem(SKIP_KEY, '1');
  } catch {
    // localStorage no disponible (modo privado en algunos navegadores).
  }
}

// Campos reales de la config de integración devuelta por
// `/api/leads/integrations/overview` (ver backend `schemas/lead.py`):
// `is_enabled` (toggle de sync), `has_access_token` (hay credencial guardada),
// `property_id`/`shop_domain` (identificador conectado). La señal de "fuente
// conectada" para el onboarding es tener credencial o estar habilitada — NO
// solo `is_enabled`, que puede quedar en false tras conectar por OAuth.
function configIndicatesConnection(config: unknown): boolean {
  if (!config || typeof config !== 'object') return false;
  const c = config as {
    is_enabled?: boolean | null;
    has_access_token?: boolean | null;
    property_id?: string | null;
    shop_domain?: string | null;
  };
  return (
    c.is_enabled === true ||
    c.has_access_token === true ||
    (typeof c.property_id === 'string' && c.property_id.length > 0)
  );
}

export function isProviderConnected(entry: unknown): boolean {
  if (!entry || typeof entry !== 'object') return false;
  const maybe = entry as { connected?: boolean; config?: unknown };
  if (maybe.connected === true) return true;
  // Soporta tanto el entry `{ config, runs }` como una config plana.
  return configIndicatesConnection(maybe.config ?? entry);
}

export function countConnectedIntegrations(overview: unknown): number {
  if (!overview || typeof overview !== 'object') return 0;
  let total = 0;
  for (const key of ['meta', 'shopify', 'google_analytics']) {
    const entry = (overview as Record<string, unknown>)[key];
    if (Array.isArray(entry)) {
      if (entry.some((item) => isProviderConnected(item))) total += 1;
    } else if (isProviderConnected(entry)) {
      total += 1;
    }
  }
  return total;
}

export interface SetupSnapshot {
  companies: number;
  users: number;
  connectedIntegrations: number;
  // Existe al menos un SyncRun con éxito (meta/shopify). Opcional por compat:
  // `isSetupComplete` no lo exige; `isMandatoryComplete` sí.
  hasSuccessfulSync?: boolean;
}

// Fuente única de verdad de "setup esencial completo", compartida por la
// página de primeros pasos. Un workspace de un solo operador (1 usuario admin)
// cuenta como completo: no exige invitar a nadie más.
export function isSetupComplete(snapshot: SetupSnapshot): boolean {
  return (
    snapshot.companies > 0 && snapshot.users > 0 && snapshot.connectedIntegrations > 0
  );
}

// Configuración ESTRICTAMENTE NECESARIA para que la app sea operativa: empresa,
// una integración conectada y un sync con éxito (sin él, reporting y métricas
// salen vacíos). Es el gate duro del onboarding: NO se puede saltar y bloquea
// la navegación global hasta completarse. El equipo es opcional, así que `users`
// no entra aquí (el propio admin ya es un usuario).
export function isMandatoryComplete(snapshot: SetupSnapshot): boolean {
  return (
    snapshot.companies > 0 &&
    snapshot.connectedIntegrations > 0 &&
    snapshot.hasSuccessfulSync === true
  );
}
